import React, { memo, useState, useEffect, useCallback } from 'react';
import { Popover, Badge, Button, Spin, Empty, Typography } from 'antd';
import { PiBellBold, PiCheckCircleBold, PiArrowsClockwiseBold } from 'react-icons/pi';
import styled from 'styled-components';
import { remote_getMyNotifications } from '~/common/typescript/apis/NotificationAPIs';
import { Notification_Base } from '~/common/typescript/models/Notification';
import { useTranslation } from 'react-i18next';

import { useAtom } from 'jotai';
import { userInformationAtom } from '~/stores/UserStore';

const { Text } = Typography;

interface NotificationBellProps {
  className?: string;
}

const NotificationBell: React.FC<NotificationBellProps> = ({ className }) => {
  const { t } = useTranslation();
  const [userInformation] = useAtom(userInformationAtom);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [notifications, setNotifications] = useState<Notification_Base[]>([]);
  const [unreadCount, setUnreadCount] = useState<number>(0);

  const fetchNotifications = useCallback(async () => {
    if (!userInformation) return;
    try {
      setLoading(true);
      const res = await remote_getMyNotifications({ page: 1, pageSize: 10 });
      if (res && res.data) {
        const items: Notification_Base[] = Array.isArray(res.data) ? res.data : [];
        setNotifications(items);
        setUnreadCount(items.length > 0 ? items.length : 0);
      }
    } catch (err) {
      console.warn('Failed to fetch notifications:', err);
    } finally {
      setLoading(false);
    }
  }, [userInformation]);

  useEffect(() => {
    if (userInformation) {
      fetchNotifications();
    }
  }, [userInformation, fetchNotifications]);

  if (!userInformation) {
    return null;
  }

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    if (newOpen) {
      fetchNotifications();
    }
  };

  const formatTime = (dateStr?: string) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('vi-VN', {
        hour: '2-digit',
        minute: '2-digit',
        day: '2-digit',
        month: '2-digit',
      });
    } catch {
      return dateStr;
    }
  };

  const popoverContent = (
    <UI_PopoverContainer>
      <UI_PopoverHeader>
        <UI_HeaderTitle>
          <PiBellBold style={{ fontSize: 16, color: 'var(--color-primary)' }} />
          <span>{t('notification') || 'Thông báo'}</span>
        </UI_HeaderTitle>
        <UI_HeaderActions>
          <Button
            type="text"
            size="small"
            icon={<PiArrowsClockwiseBold />}
            loading={loading}
            onClick={fetchNotifications}
            title="Làm mới"
          />
        </UI_HeaderActions>
      </UI_PopoverHeader>

      <UI_PopoverBody>
        {loading && notifications.length === 0 ? (
          <UI_CenterBox>
            <Spin size="small" />
          </UI_CenterBox>
        ) : notifications.length === 0 ? (
          <UI_CenterBox>
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={t('no_notification') || 'Không có thông báo mới'}
            />
          </UI_CenterBox>
        ) : (
          <UI_NotificationList>
            {notifications.map((item, index) => (
              <UI_NotificationItem key={item.id || index}>
                <UI_ItemIcon>
                  <PiCheckCircleBold />
                </UI_ItemIcon>
                <UI_ItemContent>
                  <UI_ItemTitle ellipsis={{ tooltip: item.title }}>
                    {item.title || 'Thông báo hệ thống'}
                  </UI_ItemTitle>
                  <UI_ItemText>
                    {item.content || ''}
                  </UI_ItemText>
                  {item.createdAt && (
                    <UI_ItemTime>{formatTime(item.createdAt)}</UI_ItemTime>
                  )}
                </UI_ItemContent>
              </UI_NotificationItem>
            ))}
          </UI_NotificationList>
        )}
      </UI_PopoverBody>
    </UI_PopoverContainer>
  );

  return (
    <Popover
      content={popoverContent}
      trigger="click"
      open={open}
      onOpenChange={handleOpenChange}
      placement="bottomRight"
      overlayClassName="notification-bell-popover"
      overlayInnerStyle={{ padding: 0, borderRadius: 8, overflow: 'hidden' }}
    >
      <UI_BellButton className={className} title="Thông báo">
        <Badge count={unreadCount} size="small" offset={[2, -2]}>
          <PiBellBold style={{ fontSize: 18 }} />
        </Badge>
      </UI_BellButton>
    </Popover>
  );
};

export default memo(NotificationBell);

const UI_BellButton = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 6px;
  cursor: pointer;
  color: #475569;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(59, 130, 246, 0.08);
    color: var(--color-primary);
  }
`;

const UI_PopoverContainer = styled.div`
  width: 320px;
  max-width: 90vw;
  background: #ffffff;
  display: flex;
  flex-direction: column;
`;

const UI_PopoverHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  border-bottom: 1px solid #f1f5f9;
  background: #fafafa;
`;

const UI_HeaderTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  font-size: 14px;
  color: #1e293b;
`;

const UI_HeaderActions = styled.div`
  display: flex;
  align-items: center;
`;

const UI_PopoverBody = styled.div`
  max-height: 360px;
  overflow-y: auto;
`;

const UI_CenterBox = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 28px 16px;
`;

const UI_NotificationList = styled.div`
  display: flex;
  flex-direction: column;
`;

const UI_NotificationItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 14px;
  border-bottom: 1px solid #f8fafc;
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover {
    background: #f8fafc;
  }

  &:last-child {
    border-bottom: none;
  }
`;

const UI_ItemIcon = styled.div`
  color: var(--color-primary);
  font-size: 16px;
  margin-top: 2px;
  flex-shrink: 0;
`;

const UI_ItemContent = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
`;

const UI_ItemTitle = styled(Text)`
  font-size: 13px;
  font-weight: 600;
  color: #334155;
  margin-bottom: 2px;
`;

const UI_ItemText = styled.div`
  font-size: 12px;
  color: #64748b;
  line-height: 1.4;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;

const UI_ItemTime = styled.span`
  font-size: 11px;
  color: #94a3b8;
  margin-top: 4px;
`;
